import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, EMPTY, filter, map, Observable, tap } from 'rxjs';
import { SurveyOptions } from '../../features';
import {
  isConditional,
  isNoQuestion,
  pickAnsweredQuestions,
  shouldAutoScrollQuestions,
} from '../../helpers';
import {
  QuestionInputType,
  SurveyPage,
  SurveyQuestion,
  SurveyTemplate,
} from '../../models';
import { SafeHtmlPipe } from '../../pipes';
import { PageForm } from '../../services';
import { filterNil } from '../../store';
import { AutocompleteInputComponent } from '../autocomplete-input/autocomplete-input.component';
import { EmailInputComponent } from '../email-input/email-input.component';
import { InputValidatedDataComponent } from '../input-validated-data/input-validated-data.component';
import { MedicationsComponent } from '../medications/medications.component';
import { NumberInputComponent } from '../number-input/number-input.component';
import { RadioInputComponent } from '../radio-input/radio-input.component';
import { RangePickerComponent } from '../range-picker/range-picker.component';
import { SelectInputComponent } from '../select-input/select-input.component';
import { StepPickerComponent } from '../step-picker/step-picker.component';
import { SurveyPageHeaderComponent } from '../survey-page-header/survey-page-header.component';
import { TextInputComponent } from '../text-input/text-input.component';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import { slowScrollTo } from '../utils';

interface Question extends SurveyQuestion {
  isQuestion: boolean;
  isConditional: boolean;
}

@UntilDestroy()
@Component({
  selector: 'lib-survey-body',
  templateUrl: './survey-body.component.html',
  styleUrls: ['./survey-body.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    EmailInputComponent,
    MedicationsComponent,
    NumberInputComponent,
    RadioInputComponent,
    RangePickerComponent,
    SelectInputComponent,
    StepPickerComponent,
    // GroupRadioInputComponent,
    // GroupSelectInputComponent,
    TextInputComponent,
    TimePickerComponent,
    AutocompleteInputComponent,
    SurveyPageHeaderComponent,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    InputValidatedDataComponent,
    SafeHtmlPipe,
  ],
})
export class SurveyBodyComponent implements OnInit {
  @Input({ required: true }) template!: SurveyTemplate;
  @Input({ required: true }) page!: SurveyPage;
  @Input({ required: true }) form!: PageForm;
  @Input() options?: SurveyOptions;
  @Input() isLastPage = false;
  @Input() isDisabled = false;
  @Input() isScrolling$: Observable<boolean> = EMPTY;

  @Output() next = new EventEmitter();
  @Output() previous = new EventEmitter();
  @Output() send = new EventEmitter();

  readonly QuestionInputType = QuestionInputType;

  questions!: Question[];

  ngOnInit() {
    const questions = this.page.questions ?? [];

    this.questions = questions.map((question) => ({
      ...question,
      isQuestion: !isNoQuestion(question),
      isConditional: isConditional(question),
    }));

    if (shouldAutoScrollQuestions(questions)) {
      this.autoscrollToNextQuestion().pipe(untilDestroyed(this)).subscribe();
    }
  }

  autoscrollToNextQuestion() {
    return this.form.valueChanges.pipe(
      // Get the 1 question that has just been answered
      pickAnsweredQuestions(this.form.value),
      filter((references) => references.length === 1),
      map((references) => references[0]),
      // get next question
      map((answer) => {
        const questions = this.page.questions;
        const index = questions.findIndex(
          ({ reference }) => reference === answer
        );
        return questions[index + 1];
      }),
      filterNil(),
      // get element
      map((nextQuestion) => this.getQuestionChild(nextQuestion.reference)),
      filterNil(),
      // scroll
      debounceTime(200),
      tap((nextQuestion) => {
        slowScrollTo(nextQuestion.nativeElement);
      })
    );
  }

  onPrevious() {
    this.previous.emit();
  }

  onNext() {
    const form = this.form;
    if (!form.valid && !form.disabled) {
      return;
    }

    this.isLastPage && this.options?.submitOnLastQuestion
      ? this.send.emit()
      : this.next.emit();
  }

  displayErrors() {
    const form = this.form;
    form.markAllAsTouched();
    Object.values(form.controls).forEach((control) =>
      control.updateValueAndValidity({ onlySelf: true })
    );

    const firstError = Object.entries(form.controls).find(
      ([_reference, control]) => control.invalid
    );
    if (firstError) {
      const [reference] = firstError;
      const child = this.getQuestionChild(reference);
      if (child) {
        slowScrollTo(child.nativeElement);
      }
    }
  }

  @ViewChildren('question') questionChilren!: QueryList<
    ElementRef<HTMLElement>
  >;

  getQuestionChild(reference: string) {
    return this.questionChilren.find((el) => el.nativeElement.id === reference);
  }
}
